import { useEffect, useMemo, useRef } from 'react'
import { useMarketplaceOrderSnapshot, useOrderMessageSnapshot } from '../../generated/prototypeRuntime'
import { useScopeLoaded } from '../../lib/projectionHealth'
import { useChooseChow } from '../choosechow/ChooseChowContext'
import { ProjectionCueTracker } from './events'
import { incomingOrders, RING_EVERY_MS, shouldRing, waitingOrderIds } from './incomingOrders'
import { pushState } from './push'
import { readSoundPreferences, SOUND_CUES, SoundPlayer, type SoundCue, type SoundPreferences } from './sounds'

type Snapshot = { records: readonly Record<string, unknown>[] }

export function FeedbackBridge() {
  const { profileId, isChef } = useChooseChow()
  const orders = useMarketplaceOrderSnapshot() as Snapshot
  const messages = useOrderMessageSnapshot() as Snapshot
  const ordersLoaded = useScopeLoaded('marketplace', 'marketplace_orders')
  const messagesLoaded = useScopeLoaded('message', 'order_messages')
  const player = useRef<SoundPlayer | null>(null)
  const tracker = useRef<ProjectionCueTracker | null>(null)
  const preferences = useRef(readSoundPreferences(profileId))

  useEffect(() => {
    const audio = new SoundPlayer()
    player.current = audio
    tracker.current = new ProjectionCueTracker()
    preferences.current = readSoundPreferences(profileId)
    void pushState(profileId, true).catch(() => {})
    const unlock = () => { if (preferences.current.enabled) void audio.unlock() }
    const feedback = (event: Event) => {
      const detail = (event as CustomEvent<{ cue: SoundCue; key: string }>).detail
      if (detail && SOUND_CUES.includes(detail.cue) && typeof detail.key === 'string') audio.play(detail.cue, detail.key, preferences.current)
    }
    const setting = (event: Event) => {
      const detail = (event as CustomEvent<{ profileId: string; preferences: SoundPreferences }>).detail
      if (detail?.profileId === profileId) preferences.current = detail.preferences
      if (!preferences.current.enabled) audio.pause()
    }
    const storage = () => { preferences.current = readSoundPreferences(profileId); if (!preferences.current.enabled) audio.pause() }
    // A chef's kitchen screen keeps its audio alive in the background so a new
    // order still rings; a customer's goes quiet when they look away.
    const visibility = () => { if (document.visibilityState !== 'visible' && !isChef) audio.pause() }
    window.addEventListener('pointerdown', unlock, { passive: true })
    window.addEventListener('keydown', unlock)
    window.addEventListener('chow:feedback', feedback)
    window.addEventListener('chow:sound-preferences', setting)
    window.addEventListener('storage', storage)
    document.addEventListener('visibilitychange', visibility)
    return () => {
      window.removeEventListener('pointerdown', unlock)
      window.removeEventListener('keydown', unlock)
      window.removeEventListener('chow:feedback', feedback)
      window.removeEventListener('chow:sound-preferences', setting)
      window.removeEventListener('storage', storage)
      document.removeEventListener('visibilitychange', visibility)
      audio.dispose()
      player.current = null
    }
  }, [profileId, isChef])

  // Paid orders the kitchen has not accepted keep the bell ringing.
  const waiting = useMemo(
    () => (isChef && ordersLoaded ? waitingOrderIds(orders.records, profileId) : []),
    [isChef, ordersLoaded, orders.records, profileId]
  )
  const waitingKey = waiting.join(',')
  useEffect(() => { incomingOrders.setWaiting(waiting) }, [waiting])
  useEffect(() => () => incomingOrders.reset(), [])
  useEffect(() => {
    if (!waitingKey) return
    const timer = window.setInterval(() => {
      if (!preferences.current.enabled || !shouldRing(incomingOrders.snapshot(), Date.now())) return
      if (player.current?.ring(preferences.current)) {
        try { navigator.vibrate?.([180, 90, 180, 90, 260]) } catch { /* no vibration motor */ }
      }
    }, RING_EVERY_MS)
    return () => window.clearInterval(timer)
  }, [waitingKey])

  useEffect(() => {
    if (!ordersLoaded || !messagesLoaded || !profileId) return
    for (const notif of tracker.current?.scan(orders.records, messages.records, profileId, isChef) ?? []) {
      player.current?.play(notif.cue, notif.key, preferences.current)
      window.dispatchEvent(new CustomEvent('chow:app-notification', { detail: notif }))
    }
  }, [orders.records, messages.records, profileId, isChef, ordersLoaded, messagesLoaded])
  return null
}
